import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureBoundaryError } from '../services/errorTracker';
import { isDynamicImportChunkError, clearChunkReloadFlag } from '../services/lazyWithRetry';
import { AlertTriangle, RefreshCw, Copy, Check, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  children: ReactNode;
  /** Panel name shown in the error state */
  panelName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
  showDetails: boolean;
}

/**
 * High-resilience glassmorphic error boundary for Scrudix OS dashboard panels.
 * Isolates crashes so one broken panel doesn't take down the entire workspace.
 */
class PanelErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[Scrudix] Panel "${this.props.panelName || 'Unknown'}" crashed:`, error, errorInfo);
    this.setState({ errorInfo });
    captureBoundaryError(error, errorInfo.componentStack ?? undefined);
  }

  handleRetry = () => {
    if (isDynamicImportChunkError(this.state.error)) {
      clearChunkReloadFlag();
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null, copied: false, showDetails: false });
  };

  handleCopyDiagnostics = () => {
    const { error, errorInfo } = this.state;
    const diagnostics = [
      `=== Scrudix Panel Crash Report ===`,
      `Panel: ${this.props.panelName || 'Unknown Panel'}`,
      `Timestamp: ${new Date().toISOString()}`,
      `Error: ${error?.name || 'Error'}: ${error?.message || 'Unknown error'}`,
      `\n--- Stack Trace ---`,
      error?.stack || 'No stack trace available',
      errorInfo?.componentStack ? `\n--- Component Stack ---\n${errorInfo.componentStack}` : '',
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(diagnostics);
    this.setState({ copied: true });
    toast.success('Diagnostics copied to clipboard');
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  render() {
    if (this.state.hasError) {
      const { panelName } = this.props;
      const { error, copied, showDetails } = this.state;

      return (
        <div className="relative my-2 rounded-2xl border border-red-500/25 bg-slate-900/90 dark:bg-zinc-950/90 p-5 shadow-xl backdrop-blur-xl text-slate-100 overflow-hidden">
          {/* Subtle glow background */}
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />

          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-400 border border-red-500/30">
              <AlertTriangle size={18} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-md bg-red-500/20 text-red-300 font-mono text-[10px] font-bold uppercase tracking-wider border border-red-500/30">
                  Panel Fault
                </span>
                <h4 className="text-xs font-bold text-slate-100 truncate">
                  {panelName || 'Component'} encountered an isolated error
                </h4>
              </div>

              <p className="mt-1 font-mono text-[11px] text-red-200/80 line-clamp-2 leading-relaxed bg-black/30 p-2 rounded-lg border border-white/5">
                {error?.message || 'An unexpected rendering error occurred.'}
              </p>

              {/* Action buttons */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={this.handleRetry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-md transition active:scale-95 cursor-pointer"
                >
                  <RefreshCw size={12} />
                  <span>Reload Panel</span>
                </button>

                <button
                  onClick={this.handleCopyDiagnostics}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-xs font-medium text-slate-300 transition cursor-pointer"
                  title="Copy diagnostics for developer support"
                >
                  {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  <span>{copied ? 'Copied' : 'Diagnostics'}</span>
                </button>

                <button
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200 transition ml-auto cursor-pointer"
                >
                  <span>{showDetails ? 'Hide Trace' : 'View Trace'}</span>
                  {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
              </div>

              {/* Collapsible Details */}
              {showDetails && error?.stack && (
                <div className="mt-3 p-2.5 rounded-xl border border-white/10 bg-black/60 font-mono text-[10px] text-slate-400 overflow-x-auto max-h-40 lumi-scrollbar">
                  <pre className="whitespace-pre-wrap leading-relaxed">{error.stack}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PanelErrorBoundary;
